import { FC, useEffect, useRef, useState } from "react";
import { useDataTableStore } from "../../stores/DataTableStore";
import { KTSVG } from "../../_metronic/helpers";
import { useSettingsStore } from "../../stores/SettingsStore";
import { DataTableSelect } from "../DataTable/DataTableSelect";
import { SelectOption } from "../../interfaces/SelectOption";
import { useIntl } from "react-intl";
import { useMerchantStore } from "../../stores/MerchantStore";
import useSWR from "swr";
import axios from "axios";
import useAccessToken from "../../hooks/AccessToken";
import { User } from "../../interfaces/User";
import { Merchant } from "../../interfaces/Merchant";
import useCheckIsMobile from "../../hooks/useCheckIsMobile";
import { Drawer } from "rsuite";

const API_URL = process.env.REACT_APP_API_URL

interface FilterMerchantPICProps {
    merchantTemplateId: string
}
const FilterMerchantPIC: FC<FilterMerchantPICProps> = ({ merchantTemplateId }) => {
    const { setSelectedFilter, setFilteredTablesData, tablesData, selectedFilters } = useDataTableStore();
    const { setTempSortTableData } = useMerchantStore()
    const { settings } = useSettingsStore()
    const [allUserData, setAllUserData] = useState<SelectOption[]>([]);
    const [allMerchantData, setAllMerchantData] = useState<SelectOption[]>([]);
    const intl = useIntl()
    const token = useAccessToken()
    const { isMobile } = useCheckIsMobile()
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const fetcher = (url: string) => axios.get(url, {
        headers: {
            'X-Auth-token': token
        }
    }).then(res => res.data.data)

    const selectFilterConfigs = [
        {
            placeholder: `${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${settings.merchant_title}`,
            tableKey: 'pic-merchant',
            filterKey: 'merchantName',
            options: allMerchantData,
            onChange: (values: string[]) => setSelectedFilter('pic-merchant', 'merchantName', values)
        },
        {
            placeholder: `${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.PIC_BEFORE" })}`,
            tableKey: 'pic-merchant',
            filterKey: 'userNameBefore',
            options: allUserData,
            onChange: (values: string[]) => setSelectedFilter('pic-merchant', 'userNameBefore', values)
        },
        {
            placeholder: `${intl.formatMessage({ id: "FORM.ACTION.CHOOSE" })} ${intl.formatMessage({ id: "MASTERS.DATATABLE.COLUMNS.PIC_AFTER" })}`,
            tableKey: 'pic-merchant',
            filterKey: 'userNameAfter',
            options: allUserData,
            onChange: (values: string[]) => setSelectedFilter('pic-merchant', 'userNameAfter', values)
        },
    ]

    const { data: allUsers = [], error: usersError, isLoading: usersLoading } = useSWR(`${API_URL}user/company`, fetcher)
    const { data: allMerchants = [], error: merchantsError, isLoading: merchantsLoading } = useSWR(merchantTemplateId ? `${API_URL}merchants?merchantTemplateId=${merchantTemplateId}` : null, fetcher)

    useEffect(() => {
        if (!usersLoading) {
            const allUserData = allUsers.map((user: User) => ({
                value: user.name,
                label: user.name
            }));

            setAllUserData(allUserData);
        }
    }, [allUsers]);

    useEffect(() => {
        if (!merchantsLoading) {
            const allMerchantData = allMerchants.map((merchant: Merchant) => ({
                value: merchant.name,
                label: merchant.name
            }));

            setAllMerchantData(allMerchantData);
        }
    }, [allMerchants]);

    const handleResetFilter = () => {
        setFilteredTablesData('pic-merchant', tablesData[`pic-merchant`])
        setTempSortTableData([], 'desc')
        setSelectedFilter('pic-merchant', 'merchantName', [])
        setSelectedFilter('pic-merchant', 'userNameBefore', [])
        setSelectedFilter('pic-merchant', 'userNameAfter', [])
        setTempSortTableData([], 'desc')
    }

    useEffect(() => {
        const removeErrorClass = () => {
            const errorPickers = document.querySelectorAll('.rs-picker-error');

            errorPickers.forEach(picker => {
                picker.classList.remove('rs-picker-error');
            });
        };

        removeErrorClass();

        const intervalId = setInterval(removeErrorClass, 100);

        const handlePickerOpen = () => {
            setTimeout(removeErrorClass, 10);
        };

        document.addEventListener('click', handlePickerOpen);

        return () => {
            clearInterval(intervalId);
            document.removeEventListener('click', handlePickerOpen);
        };
    }, []);

    const handleOpenDrawer = () => {
        setIsDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setIsDrawerOpen(false);
    };

    return (
        <>
            {
                ((selectedFilters[`pic-merchant`] && (selectedFilters[`pic-merchant`]['merchantName']?.length > 0 || selectedFilters[`pic-merchant`]['userNameBefore']?.length > 0 || selectedFilters[`pic-merchant`]['userNameAfter']?.length > 0))) ?
                    <>
                        <button className="btn mt-3 me-3" style={{ backgroundColor: "#f1faff", border: "2px solid #009EF7", padding: "calc(0.75rem) calc(1.5rem)" }} onClick={handleOpenDrawer}>
                            <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-2 svg-icon-primary m-0' />
                        </button>
                        <button type="button"
                            className="btn btn-white mt-3 text-primary"
                            onClick={() => { handleResetFilter() }}
                        >
                            Reset Filter
                        </button>
                    </>
                    :
                    <button type="button"
                        className="btn btn-light mt-3"
                        id="drawer_filter_merchant_pic"
                        onClick={handleOpenDrawer}
                    >
                        <KTSVG path='/media/icons/duotune/general/gen031.svg' className='svg-icon-2 m-0' />
                    </button>
            }

            <Drawer
                className="bg-white"
                size={isMobile ? "xs" : "sm"}
                placement="right"
                open={isDrawerOpen}
                onClose={handleCloseDrawer}
                style={{ height: "100%", overflowY: "auto" }}
            >
                <div className="card w-100 rounded-0" style={{ height: "100%" }} id="kt_drawer_filter_merchant_pic">
                    <div className="card-header pe-5">
                        <div className="card-title">
                            <div className="d-flex justify-content-center flex-column me-3">
                                <span className="fs-4 fw-bolder text-gray-900 text-hover-primary me-1 lh-1">Filter</span>
                            </div>
                        </div>
                        <div className="card-toolbar">
                            <div className="btn btn-sm btn-icon btn-active-light-primary" onClick={handleCloseDrawer}>
                                <span className="svg-icon svg-icon-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black"></rect>
                                        <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black"></rect>
                                    </svg>
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="card-body" style={{ overflowY: "auto" }}>
                        <div>
                            <div style={{ fontWeight: "600" }}>Filter Data</div>
                            <div className="separator separator-dashed my-3"></div>
                            {
                                selectFilterConfigs.map((config, index) => (
                                    <DataTableSelect
                                        placeholder={config.placeholder}
                                        tableKey={config.tableKey}
                                        filterKey={config.filterKey}
                                        key={index}
                                        options={config.options}
                                        onChange={config.onChange}
                                    />
                                ))
                            }
                        </div>
                    </div>
                </div>
            </Drawer>
        </>
    )
}

export { FilterMerchantPIC }